import { useEffect, useReducer } from "react";
import { useSearchParams } from "react-router-dom";
import { ListFilters } from "../types";

type Action =
  | { type: "SET_PAGE"; page: number }
  | { type: "SET_SEARCH_TERM"; term: string }
  | { type: "SYNC_FROM_URL"; page: number; q?: string };

const reducer = (state: ListFilters, action: Action): ListFilters => {
  switch (action.type) {
    case "SET_PAGE":
      return { ...state, page: action.page };
    case "SET_SEARCH_TERM":
      return { ...state, q: action.term, page: 1 };
    case "SYNC_FROM_URL":
      return { ...state, page: action.page, q: action.q };
  }
};

export interface UseListFiltersReturn {
  state: ListFilters;
  nextPage: () => void;
  prevPage: () => void;
  setPage: (page: number) => void;
  setSearchTerm: (term: string) => void;
}

interface UseListFiltersOptions {
  initialState: ListFilters;
  // Namespaces the "q"/"page" URL params (e.g. "q_handles"/"page_handles"). Required whenever
  // more than one useListFilters instance can be mounted at once under the same URL (e.g. sibling
  // Tabs contents) — otherwise they all read/write the same "q"/"page" params and clobber each other.
  key?: string;
}

const useListFilters = (userOptions?: Partial<UseListFiltersOptions>): UseListFiltersReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const qParamName = userOptions?.key ? `q_${userOptions.key}` : "q";
  const pageParamName = userOptions?.key ? `page_${userOptions.key}` : "page";

  const initialState: ListFilters = {
    page: Number(searchParams.get(pageParamName) ?? 1),
    limit: 20,
    q: searchParams.get(qParamName) ?? undefined,
  };

  const options: UseListFiltersOptions = {
    initialState,
    ...userOptions,
  };
  const [state, dispatch] = useReducer(reducer, options.initialState);

  // Only ever written in direct response to a user action (paging/searching) below —
  // never reactively from a useEffect on state, since that would also fire on mount
  // (e.g. a lazily-mounted sibling tab backfilling its default page into the URL) and
  // could race with an unrelated, concurrent navigation such as a tab switch.
  const writeToUrl = (updates: { page?: number; q?: string }) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (updates.page !== undefined) {
        next.set(pageParamName, String(updates.page));
      }
      if ("q" in updates) {
        if (updates.q) {
          next.set(qParamName, updates.q);
        } else {
          next.delete(qParamName);
        }
      }
      return next;
    });
  };

  const setPage = (page: number) => {
    dispatch({ type: "SET_PAGE", page });
    writeToUrl({ page });
  };

  // Sync FROM the URL only (e.g. browser back/forward navigation) — never writes.
  useEffect(() => {
    const page = Number(searchParams.get(pageParamName));
    const validPage = page > 0 ? page : 1;
    const q = searchParams.get(qParamName) ?? undefined;
    if (validPage !== state.page || q !== state.q) {
      dispatch({ type: "SYNC_FROM_URL", page: validPage, q });
    }
  }, [searchParams]);

  return {
    state,
    nextPage: () => setPage(state.page + 1),
    prevPage: () => state.page > 1 && setPage(state.page - 1),
    setPage,
    setSearchTerm: (term: string) => {
      dispatch({ type: "SET_SEARCH_TERM", term });
      writeToUrl({ page: 1, q: term });
    },
  };
};

export default useListFilters;
